import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError } from "../lib/api";
import { MessageSquareWarning, ShieldCheck, FileText, Lock, ArrowRight, Loader2, AlertTriangle, BadgeCheck } from "lucide-react";

const CATEGORIES = [
  ["vehicle_sale", "Vehicle Sale", "بيع مركبة"],
  ["rental", "Rental", "تأجير"],
  ["financing", "Financing", "تمويل"],
  ["insurance", "Insurance", "تأمين"],
  ["inspection", "Inspection", "فحص"],
  ["delivery", "Delivery", "توصيل"],
  ["parts", "Spare Parts", "قطع غيار"],
  ["service", "Service", "صيانة"],
  ["auction", "Auction", "مزاد"],
];

export function DisputeCenterPage() {
  const { user } = useAuth();
  const { lang, t } = useI18n();
  const [form, setForm] = useState({ category: "vehicle_sale", subject: "", description: "", against_party: "" });
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [err, setErr] = useState(null);

  const load = () => apiClient.get("/disputes/mine").then(({ data }) => setList(data.items || [])).catch(() => {});

  useEffect(() => { if (user) load(); }, [user]);

  if (user === undefined) return <div className="p-10 text-[#6b6b6b]">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/disputes" }} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await apiClient.post("/disputes", form);
      setForm({ category: "vehicle_sale", subject: "", description: "", against_party: "" });
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="dispute-center-page">
      <div className="overline mb-2 inline-flex items-center gap-2"><MessageSquareWarning className="w-3 h-3" /> DISPUTE RESOLUTION CENTER</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium">{lang === "ar" ? "مركز حل النزاعات" : "Dispute Resolution Center"}</h1>
      <p className="text-[#6b6b6b] mt-2 max-w-2xl">{lang === "ar" ? "وثّق نزاعك، ارفع الأدلة، وتابع الحالة. تعمل كارتال كمنصة محايدة لتسهيل التواصل." : "Document your dispute, upload evidence and track status. CARTAL acts as a neutral platform to facilitate communication."}</p>

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 mt-10">
        <form onSubmit={submit} className="surface rounded-3xl p-8 space-y-3" data-testid="dispute-form">
          <div className="overline">SUBMIT A DISPUTE</div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الفئة" : "Category"}</label>
            <select className="input-dark mt-1" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
              {CATEGORIES.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الموضوع" : "Subject"}</label>
            <input className="input-dark mt-1" required value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الطرف الآخر" : "Against party"}</label>
            <input className="input-dark mt-1" value={form.against_party} onChange={(e) => setForm({...form, against_party: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الوصف" : "Description"}</label>
            <textarea className="input-dark mt-1" rows={5} required value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
          </div>
          {err && <div className="text-sm text-red-300">{err}</div>}
          <button className="btn-primary w-full justify-center" disabled={busy} data-testid="dispute-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {lang === "ar" ? "ارفع النزاع" : "Submit dispute"}
          </button>
          <p className="text-xs text-[#6b6b6b] leading-relaxed mt-2">
            {lang === "ar"
              ? "كارتال ليست طرفاً في النزاع. الدور هو تسهيل التواصل والاحتفاظ بالسجلات."
              : "CARTAL is not a party to the dispute. Our role is to facilitate communication and archive records."}
          </p>
        </form>

        <div>
          <div className="overline mb-4">MY DISPUTES</div>
          {list.length === 0 ? (
            <div className="surface rounded-2xl p-10 text-center text-[#6b6b6b]">{lang === "ar" ? "لا توجد نزاعات بعد." : "No disputes yet."}</div>
          ) : (
            <div className="space-y-3">
              {list.map((d) => (
                <div key={d.id} className="surface rounded-2xl p-5" data-testid={`dispute-${d.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-display text-lg">{d.subject}</div>
                    <span className="pill pill-cyan">{d.status}</span>
                  </div>
                  <div className="text-xs text-[#6b6b6b] mt-1">{d.category} · {new Date(d.created_at).toLocaleDateString()}</div>
                  <p className="text-sm text-[#525252] mt-3 line-clamp-3">{d.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function TrustSafetyPage() {
  const { lang } = useI18n();
  const items = [
    { icon: BadgeCheck, t: lang === "ar" ? "التحقق من الهوية الوطنية" : "National ID verification", d: lang === "ar" ? "نتحقق من هوية كل بائع فردي قبل التفعيل." : "Every individual seller verified before listing." },
    { icon: ShieldCheck, t: lang === "ar" ? "السجل التجاري" : "Commercial registration check", d: lang === "ar" ? "كل وكالة وتاجر يتم التأكد من سجله التجاري." : "Every dealer and agency CR verified." },
    { icon: Lock, t: lang === "ar" ? "ضمان الدفع (Escrow)" : "Escrow payments", d: lang === "ar" ? "تُحرّر الأموال بعد إتمام الفحص والاستلام." : "Funds released only after inspection and delivery." },
    { icon: AlertTriangle, t: lang === "ar" ? "كشف الاحتيال بالذكاء الاصطناعي" : "AI fraud detection", d: lang === "ar" ? "نحلل الإعلانات تلقائياً لرصد الأنماط المشبوهة." : "We analyse listings automatically for suspicious patterns." },
    { icon: FileText, t: lang === "ar" ? "تقارير الفحص" : "Inspection reports", d: lang === "ar" ? "تقارير مستقلة من شركاء معتمدين." : "Independent reports from accredited partners." },
    { icon: MessageSquareWarning, t: lang === "ar" ? "بلاغ مستخدم" : "User reporting", d: lang === "ar" ? "بلاغ سريع وحظر فوري للحسابات المخالفة." : "Fast reporting and suspension of policy-violating accounts." },
  ];
  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="trust-page">
      <div className="overline mb-2">TRUST & SAFETY CENTER</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium">{lang === "ar" ? "ثقة مبنية على الأنظمة" : "Trust, engineered into every flow"}</h1>
      <p className="text-[#525252] mt-3 max-w-2xl">{lang === "ar" ? "نقدم طبقات أمان متعددة لكل معاملة على كارتال." : "We layer multiple safety controls into every transaction on CARTAL."}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <div key={i} className="surface p-6">
              <div className="w-11 h-11 rounded-xl bg-[#c9a55c]/15 border border-[#c9a55c]/40 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-[#8a6f2e]" /></div>
              <h3 className="font-display text-xl">{it.t}</h3>
              <p className="text-sm text-[#525252] mt-2 leading-relaxed">{it.d}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
